import { ErrorBoundary } from '@sentry/react';
import { KitbagAISummaryProvider } from '@statsbomb/kitbag-ai-summary';
import { AuthProtected } from '@statsbomb/kitbag-auth';
import { iqTheme, ThemeProvider } from '@statsbomb/kitbag-themes';
import { useQueryClient } from '@tanstack/react-query';
import { Provider as JotaiProvider } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import { ReactNode, Suspense } from 'react';
import { useIntl } from 'react-intl';
import env from '@/env';
import { useAISummaryMetricTranslations, useAISummaryTranslations } from '@/hooks/useAISummaryTranslations';
import { useSyncLocationAtom } from '@/hooks/useSyncLocationAtom';
import { AppErrorBoundaryFallback } from '../HandleInvalidEntityUser';
import { AppShellNoTextLoadingFallback } from '../layout/AppShellFallback';
import { VideoProvider } from '../VideoProvider';
import { FetchProvider } from './FetchProvider';
import { KitbagGlobalFootballProvider } from './KitbagGlobalFootballProvider';
import { KitbagShortlistsProvider } from './KitbagShortlistsProvider';
import { PrefetchNavigationData } from './PrefetchNavigationData';
import { QualtricsProvider } from './QualtricsProvider';
import { SessionReplayIdentityProvider } from './SessionReplayIdentityProvider';
import { SnowplowProvider } from './SnowplowProvider';

const InitJotaiQueryClient = ({ children }: { children: ReactNode }) => {
  // Same instance as the QueryClientProvider in Providers.tsx, resolved from context.
  const queryClient = useQueryClient();
  // Hydration for Jotai does not care if it's server to browser/client, it is for initialising atoms.
  // https://jotai.org/docs/extensions/query#referencing-the-same-instance-of-query-client-in-your-project
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  useSyncLocationAtom();
  return children;
};

// We needed to separate the theme provider so we could pass in the Jotai state
// Jotai state must be defined below the provider or it doesn't work
const KitbagThemeProvider = ({ children }: { children: ReactNode }) => (
  // Hardcoding the theme to light for now, as we've removed the ability to change it in the UI.
  <ThemeProvider theme={iqTheme.light}>{children}</ThemeProvider>
);

/**
 * The provider tree below the auth provider: everything the app needs once Auth0 is in place. It must be
 * rendered inside an Auth0 context, a QueryClientProvider (InitJotaiQueryClient reads the client from it),
 * the I18nProvider (useIntl) and the router.
 */
export const AppProviders = ({ children }: { children: ReactNode }) => {
  const { locale } = useIntl();
  const aiSummaryTranslations = useAISummaryTranslations();
  const metricsTranslations = useAISummaryMetricTranslations();

  return (
    <JotaiProvider>
      <InitJotaiQueryClient>
        <KitbagThemeProvider>
          <VideoProvider>
            <KitbagShortlistsProvider>
              <KitbagAISummaryProvider
                agentEndpoint={env.VITE_AI_SUMMARY_AGENT_ENDPOINT ?? ''}
                soccerApiBaseUrl={env.VITE_PARACHUTE_REST_API_ENDPOINT}
                language={locale || undefined}
                translations={aiSummaryTranslations}
                metricsTranslations={metricsTranslations}
              >
                <ErrorBoundary fallback={AppErrorBoundaryFallback}>
                  <AuthProtected loading={<AppShellNoTextLoadingFallback />}>
                    <Suspense fallback={<AppShellNoTextLoadingFallback />}>
                      <FetchProvider>
                        <PrefetchNavigationData>
                          <SessionReplayIdentityProvider>
                            <QualtricsProvider>
                              <SnowplowProvider>
                                <KitbagGlobalFootballProvider>{children}</KitbagGlobalFootballProvider>
                              </SnowplowProvider>
                            </QualtricsProvider>
                          </SessionReplayIdentityProvider>
                        </PrefetchNavigationData>
                      </FetchProvider>
                    </Suspense>
                  </AuthProtected>
                </ErrorBoundary>
              </KitbagAISummaryProvider>
            </KitbagShortlistsProvider>
          </VideoProvider>
        </KitbagThemeProvider>
      </InitJotaiQueryClient>
    </JotaiProvider>
  );
};
